import React, { useState } from 'react';
import { Loader2, Sparkles, RotateCcw, Check } from 'lucide-react';
import { colorChangeAPI } from '../api';
import toast from 'react-hot-toast';

/**
 * Color-swatch picker that sits below a try-on result. Tapping a swatch fires
 * `POST /api/color/change` and swaps the displayed image to the recolored
 * version. Cache HITs return instantly and are free.
 *
 * Renders nothing if the product doesn't have color-change enabled or has
 * fewer than 2 valid color variants.
 *
 * Props:
 *   tryonKey: string — the S3 key of the source try-on output (from tryonResult)
 *   product: { sku_id, color_variants:[{name,hex_code}], color_change_enabled,
 *             color_change_quality, dress_type }
 *   currentImageUrl: string (optional) — if provided, we hide the swatch matching
 *                                       the currently shown image after a successful change
 *   onResult({ image_url, is_cache_hit, hex }): callback
 */
const TryOnColorChangePicker = ({ tryonKey, product, currentImageUrl, onResult }) => {
  const [busy, setBusy] = useState(false);
  const [activeHex, setActiveHex] = useState(null);

  if (!product || !product.color_change_enabled) return null;
  const variants = (product.color_variants || []).filter(v => v && v.hex_code);
  if (variants.length < 2) return null;
  if (!tryonKey) return null;

  // Map our internal dress_type to Maahi's enum
  const dressTypeMap = {
    top_wear: 'top',
    bottom_wear: 'bottom',
    full_wear: 'both',
    co_ord_set: 'both',
    dress: 'both',
  };
  const dressType = dressTypeMap[product.dress_type] || 'both';

  const apply = async (variant) => {
    if (busy) return;
    setBusy(true);
    setActiveHex(variant.hex_code);
    try {
      const { data } = await colorChangeAPI.change({
        sku_id: product.sku_id,
        tryon_key: tryonKey,
        target_hex: variant.hex_code,
        dress_type: dressType,
        // Mandatory by the API contract. The in-app flow uses the partner's
        // configured per-product quality so end-customers never trigger a
        // higher-cost engine than the partner authorised.
        engine: (product.color_change_quality || 'standard').toLowerCase(),
      });
      onResult?.({
        image_url: data.image_url,
        is_cache_hit: data.is_cache_hit,
        hex: variant.hex_code,
        name: variant.name,
        credits_debited: data.credits_debited,
      });
      if (data.is_cache_hit) {
        toast.success('Cached · free', { duration: 2200 });
      } else {
        toast.success(`Recolored to ${variant.name} · −${data.credits_debited} credits`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : detail;
      toast.error(msg || 'Color change failed');
      setActiveHex(null);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    onResult?.({ image_url: null, is_cache_hit: false, hex: null, name: null, reset: true });
    setActiveHex(null);
  };

  return (
    <div
      className="bg-white/90 backdrop-blur rounded-xl border border-violet-100 shadow-sm p-3"
      data-testid="tryon-color-change-picker"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
          <Sparkles className="w-3.5 h-3.5 text-violet-500" />
          Try a different color
        </div>
        {activeHex && (
          <button
            type="button"
            onClick={reset}
            disabled={busy}
            className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700"
            data-testid="tryon-color-change-reset"
          >
            <RotateCcw className="w-3 h-3" /> Original
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="tryon-color-change-swatches">
        {variants.map((v) => {
          const isActive = activeHex && v.hex_code.toLowerCase() === activeHex.toLowerCase();
          return (
            <button
              key={v.hex_code + v.name}
              type="button"
              onClick={() => apply(v)}
              disabled={busy}
              title={v.name}
              className={`group relative w-9 h-9 rounded-full border-2 transition-all ${
                isActive
                  ? 'border-violet-500 scale-110 ring-2 ring-violet-200'
                  : 'border-white shadow ring-1 ring-slate-200 hover:scale-105'
              } ${busy && !isActive ? 'opacity-40 cursor-wait' : ''}`}
              style={{ backgroundColor: v.hex_code }}
              data-testid={`color-swatch-${v.name}`}
            >
              {isActive && busy && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/20 rounded-full">
                  <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
                </span>
              )}
              {isActive && !busy && (
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-violet-500 flex items-center justify-center">
                  <Check className="w-2 h-2 text-white" strokeWidth={4} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      <p className="text-[10px] text-slate-400 mt-1.5">
        AI Color Change · cached repeats are free
      </p>
    </div>
  );
};

export default TryOnColorChangePicker;
